// The chart range control of the System pages: the NextAdmin demo `/analytics` period pills, as `FilterPills`.
import type { MetricRange } from './metric-format.js';
import { useSearchParams } from 'react-router-dom';
import { FilterPills } from '../../../components/ui/index.js';

const RANGES: ReadonlyArray<{ id: MetricRange; label: string }> = [
  { id: '1h', label: '1 h' },
  { id: '6h', label: '6 h' },
  { id: '24h', label: '24 h' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: '1y', label: '1 year' },
];

const DEFAULT_RANGE: MetricRange = '24h';

/** The chart range, kept in the URL as `?range=` so a reload or a shared link shows the same window. */
export function useRangeParam(): [MetricRange, (range: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('range');
  const range = RANGES.find((option) => option.id === requested)?.id ?? DEFAULT_RANGE;
  const setRange = (next: string) =>
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === DEFAULT_RANGE) params.delete('range');
        else params.set('range', next);
        return params;
      },
      { replace: true },
    );
  return [range, setRange];
}

/** Live ranges refresh every 30 s; a long range barely moves, so it refreshes every 5 minutes. */
export function refreshFor(range: MetricRange): number {
  return range === '1h' || range === '6h' || range === '24h' ? 30_000 : 300_000;
}

export function RangePills({ range, onChange }: { range: MetricRange; onChange: (range: string) => void }) {
  return <FilterPills aria-label="Time range" options={RANGES} value={range} onChange={onChange} />;
}
